import { Box, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { AttributionBand } from './AttributionBand';
import { useAttribution } from './useAttribution';

/**
 * Manual attribution queue - a standalone ops surface taken ahead of Phase 5. This
 * is genuinely new: the old platform assumed transactions arrived pre-attributed
 * from the networks; this queue is where transactions that arrived WITHOUT a known
 * member (orphans) get manually credited to the right member by ops.
 *
 * A virtual list over orphan transactions (NOT a CRUD resource), Cashbacks-shaped:
 * guarded review actions through the performStatusTransition + withAudit chokepoint.
 * Banded by the orphan's MERCHANT-derived market (an orphan has a market before it
 * has a member). The actions are sorted by direction of value: credit (super_admin)
 * links a member, reject and flag (editor) stop or defer.
 *
 * Crediting is a pure LINKAGE write: it sets userId, it does NOT move money. Balance
 * is derived on read from CONFIRMED transactions, so a credited orphan only counts
 * once it is separately confirmed through the existing cashbacks:resolve flow. The
 * admin authorises the linkage, never executes a wallet credit (D-manual-attribution).
 */

/** The surface without the access wrapper. Exported so tests can mount it directly. */
export const ManualAttributionView = () => {
  const { view, isLoading } = useAttribution();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Operations
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Manual attribution
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Transactions that arrived without a known member, for manual crediting.
        Crediting links a member, it does not move money: a credited transaction
        only counts once it is separately confirmed. Candidate clicks are a
        guide, the match is your judgment.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => (
          <AttributionBand key={band.market} band={band} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /manual-attribution. `attribution:read` is a read
 * capability, so viewer (read everywhere) and super_admin see it, and editor is
 * granted it explicitly (D-manual-attribution); the placeholder support / fraud
 * roles do not. The actions gate themselves: credit on attribution:credit
 * (super_admin), reject on attribution:reject and flag on attribution:flag (editor).
 */
export const ManualAttribution = () => (
  <RequireAccess capability="attribution:read">
    <ManualAttributionView />
  </RequireAccess>
);
